import type { QueryIndexResult } from 'blocks/search/search.ts';
import { fetchSearchResults } from 'blocks/search/search.ts';
import './breadcrumbs-share.css';
import './breadcrumbs-share.rebrand.css';
import { getMetadata } from 'scripts/aem.js';
import { fetchPlaceholders } from 'scripts/aem.js';
import { div } from 'scripts/dom-helpers.js';
import { DEFAULT_LANG_CODE, LANGUAGES } from 'shared/constants.ts';
import { getProductBreadcrumbPath } from 'utils/getProductBreadcrumbPath.ts';
import {
	getLanguageFromPath,
	prefixLinkWithLanguage,
} from 'utils/languageUtils.ts';
import { addLinkedDataSchema } from 'utils/linkedDataSchema/linkedDataSchema.ts';
import type { BreadcrumbList } from 'utils/linkedDataSchema/types/breadcrumbList.ts';
import type { ListItem } from 'utils/linkedDataSchema/types/listItem.ts';
import { getOrigin } from 'utils/window-utils.ts';

type Breadcrumb = {
	title?: string;
	url?: string;
	home?: boolean;
};

function fetchBreadcrumbTitle(
	queryIndexResults: QueryIndexResult[],
	path: string,
	url: string,
): Breadcrumb | undefined {
	let pathToFind = url.replace(getOrigin(), '').replace(/\/$/, '');
	let indexResult = queryIndexResults.find((r) => r.path === pathToFind);

	if (!indexResult) {
		pathToFind = pathToFind + '/';
		indexResult = queryIndexResults.find((r) => r.path === pathToFind);
		if (!indexResult) {
			console.error(`Error fetching breadcrumb for: ${url}`);
			return;
		}
	}

	return {
		title: indexResult.title || path,
		url: indexResult.path,
	};
}

async function getBreadcrumbs(document: Document) {
	let currentBreadcrumb = document.location.origin + '/';
	let allPaths = document.location.pathname
		.split('/')
		.filter((path) => path !== '');

	// Override breadcrumbs on product detail pages
	let breadcrumbsOverridden = false;
	if (document.querySelector('.product-details')) {
		const returnUrl = await getProductBreadcrumbPath();
		if (returnUrl && returnUrl !== document.location.pathname) {
			const currentPath = allPaths.pop();
			allPaths = returnUrl.split('/').filter((path) => path !== '');
			if (currentPath) {
				breadcrumbsOverridden = true;
				allPaths.push(currentPath);
			}
		}
	}

	const productsIndex = allPaths.indexOf('products');
	const breadcrumbPaths: Array<{
		path: string;
		currentBreadcrumb: string;
		homePath: boolean;
	}> = [];
	allPaths.forEach((path, index) => {
		const homePath =
			index === 0 &&
			LANGUAGES.find((l) => l === path.toLowerCase()) !== undefined;

		// Logic for product detail pages when not supplied a return_url
		if (
			!breadcrumbsOverridden &&
			productsIndex > -1 &&
			index >= productsIndex &&
			index <= productsIndex + 1
		)
			return;

		currentBreadcrumb = `${currentBreadcrumb}${path}/`;
		breadcrumbPaths.push({
			path,
			currentBreadcrumb,
			homePath,
		});
	});

	const currentPath = breadcrumbPaths.pop();

	if (
		breadcrumbPaths.length > 0 &&
		!breadcrumbPaths.find((bp) => bp.homePath)
	) {
		const lang = getLanguageFromPath() || DEFAULT_LANG_CODE;
		const path = `${lang}/`;
		breadcrumbPaths.unshift({
			path,
			currentBreadcrumb: `${document.location.origin}/${path}`,
			homePath: true,
		});
	}

	const queryIndexFile = prefixLinkWithLanguage(
		'/query-index.json?limit=100000',
	);
	const results = await fetchSearchResults(queryIndexFile);
	if (!results.length) {
		console.error('Error fetching search results');
	}

	const breadcrumbsWithTitles = await Promise.all(
		breadcrumbPaths.map((bcPath) => {
			return {
				...fetchBreadcrumbTitle(results, bcPath.path, bcPath.currentBreadcrumb),
				home: bcPath.homePath,
			};
		}),
	);

	const breadcrumbs = breadcrumbsWithTitles.filter((b) => !!b) as Breadcrumb[];

	if (currentPath) {
		breadcrumbs.push({
			title:
				document.querySelector('title')?.innerText ??
				currentPath?.path ??
				'Current Page',
			url: document.location.href.split('?')[0],
		});
	}
	return breadcrumbs;
}

function addBreadcrumbSchema(breadcrumbs: Breadcrumb[]) {
	const breadcrumbList = {
		'@type': 'BreadcrumbList',
		itemListElement: breadcrumbs.map((breadcrumb, index) => {
			return {
				'@type': 'ListItem',
				position: index + 1,
				item: {
					name: breadcrumb.title,
					'@id': breadcrumb.url?.startsWith('http')
						? breadcrumb.url
						: getOrigin() + breadcrumb.url,
				},
			} satisfies ListItem;
		}),
	} satisfies BreadcrumbList;

	addLinkedDataSchema(breadcrumbList);
}

async function buildBreadcrumbs(document: Document) {
	const breadcrumbs = await getBreadcrumbs(document);
	addBreadcrumbSchema(breadcrumbs);

	const breadcrumbsNav = document.createElement('nav');
	breadcrumbsNav.setAttribute('aria-label', 'Breadcrumb');
	const breadcrumbsContainer = document.createElement('ol');
	breadcrumbsContainer.className = 'breadcrumbs-share__breadcrumbs';
	breadcrumbsNav.append(breadcrumbsContainer);

	const placeholders = await fetchPlaceholders();
	breadcrumbs.forEach((breadcrumb, index) => {
		if (!breadcrumb.url) return;

		const breadcrumbElement = document.createElement('li');

		const pipeIndex = breadcrumb.title?.indexOf('|') ?? -1;
		const breadcrumbTitle =
			pipeIndex > -1
				? breadcrumb.title!.slice(0, pipeIndex).trim()
				: (breadcrumb.title ?? '');
		const truncatedBreadcrumbTitle =
			breadcrumbTitle.length > 30
				? `${breadcrumbTitle.substring(0, 30).trim()}...`
				: breadcrumbTitle;

		if (breadcrumb.url) {
			const link = document.createElement('a');
			link.href = breadcrumb.url;
			link.innerText = breadcrumb.home ? '' : truncatedBreadcrumbTitle;
			if (breadcrumbTitle !== truncatedBreadcrumbTitle) {
				link.title = breadcrumbTitle;
				link.setAttribute('aria-label', breadcrumbTitle);
			}

			if (breadcrumb.home) {
				link.title = placeholders.breadcrumbHomeLabel || 'Home';
				link.ariaLabel = placeholders.breadcrumbHomeLabel || 'Home';
				link.classList.add('breadcrumb-home');
			} else if (index === breadcrumbs.length - 1) {
				link.classList.add('breadcrumb-current');
				link.setAttribute('aria-current', 'location');
			}
			breadcrumbElement.append(link);
		} else if (breadcrumb.title) {
			const paragraph = document.createElement('p');
			paragraph.innerText = breadcrumbTitle;
			breadcrumbElement.append(paragraph);
		}
		breadcrumbsContainer.append(breadcrumbElement);
	});

	return breadcrumbsNav;
}

function buildShare(document: Document) {
	const shareContainer = document.createElement('nav');
	shareContainer.setAttribute('aria-label', 'Share links');
	shareContainer.className = 'breadcrumbs-share__share';

	const shareButton = document.createElement('button');
	shareButton.setAttribute('aria-label', 'Copy to clipboard');
	shareButton.className = 'share-button';
	shareButton.onclick = async () => {
		await navigator.clipboard.writeText(document.location.href);
	};
	shareContainer.append(shareButton);

	const linkedinButton = document.createElement('button');
	linkedinButton.setAttribute('aria-label', 'Share on LinkedIn');
	linkedinButton.className = 'linkedin-button';
	linkedinButton.onclick = () => {
		window.open(
			`https://www.linkedin.com/sharing/share-offsite/?url=${document.location.href}`,
			'_blank',
		);
	};
	shareContainer.append(linkedinButton);

	return shareContainer;
}

export default async function decorate(block: Element) {
	const showBreadcrumbs = getMetadata('breadcrumbs').toLowerCase() === 'true';
	const showShare = getMetadata('share').toLowerCase() === 'true';

	if (!showBreadcrumbs && !showShare) {
		block.remove();
		return;
	}

	const elements: Element[] = [];

	if (showBreadcrumbs) {
		const breadcrumbs = await buildBreadcrumbs(document);
		elements.push(breadcrumbs);
	} else {
		elements.push(document.createElement('div'));
	}

	if (showShare) {
		const share = buildShare(document);
		elements.push(share);
	}

	block.innerHTML = '';
	block.append(div(...elements));

	const section = document.querySelector('.breadcrumbs-share-container');
	const placeholder = document.querySelector('.breadcrumb-placeholder');
	if (placeholder && section) {
		placeholder.replaceWith(section);
	}
}
